<template>
    <div class="flex flex-column align-items-center min-h-screen w-full pt-6">

        <div class="text-center mb-6">
            <h2 class="Paragraph-Medium mb-2">Hey {{ instituteName }}, Let's start the journey by choosing a package</h2>
            <p class="Caption">Let's choose a package that best fits for your need, and anytime you can upgrade or downgrade your package.</p>
        </div>

        <!-- Loading packages -->
        <div v-if="packagesLoading" class="flex align-items-center justify-content-center py-8 gap-3">
            <ProgressSpinner style="width: 40px; height: 40px" />
            <span class="text-600">Loading packages...</span>
        </div>

        <InstituteSubscriptionPlans
            v-else
            mode="onboarding"
            :packages="institutePackages"
            :featureDefinitions="instituteFeatures"
            :disableActions="actionLoading"
            :currentPlanId="packageActivated ? selectedPackage?.id : null"
            @package-action="handlePackageAction"
            @contact-sales="handleContactSales"
        />

        <!-- Billing Details Dialog -->
        <BillingDetailsDialog
            v-model="showBillingDialog"
            context="paid_subscribe"
            :selected-package="selectedPackage"
            :initial-values="billingInitialValues"
            @submit="submitBilling"
        />

        <!-- Progress Dialog -->
        <Dialog
            v-model:visible="showProgressDialog"
            modal
            :closable="false"
            header="Setting up your subscription"
            :style="{ width: 'min(420px, 92vw)' }"
        >
            <div class="flex flex-column align-items-center py-4 gap-4">
                <ProgressSpinner style="width: 50px; height: 50px" />
                <p class="text-600 text-center m-0">{{ progressMessage }}</p>
            </div>
        </Dialog>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, defineEmits } from 'vue';
import InstituteSubscriptionPlans from '@/components/subscriptionPlans/instituteSubscriptionPlans.vue';
import BillingDetailsDialog from '@/components/payments/BillingDetailsForm.vue';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import { useAuthStore } from '@/stores/authStore';
import payments from '@/api/payments';
import subscriptionApi from '@/api/subscription';
import { updateInstitute } from '@/api/institutes';
import { backendApi } from '@/api/axios';
import { PACKAGES_ENDPOINTS, PAYMENT_ENDPOINTS } from '@/api/endpoints';
import { notifyError, notifyContrast } from '@/utils/notification';

const PENDING_KEY = 'instituteOnboardingPendingPackage';

const emit = defineEmits(['package-selected']);
const authStore = useAuthStore();

const instituteName = computed(() => (authStore.user?.username || '').split(' ')[0]);

// UI state
const packagesLoading    = ref(true);
const actionLoading      = ref(false);
const showBillingDialog  = ref(false);
const showProgressDialog = ref(false);
const progressMessage    = ref('');
const packageActivated   = ref(false);

// Data
const institutePackages = ref([]);
const selectedPackage   = ref(null);

const billingInitialValues = computed(() => {
    const u = authStore.user;
    if (!u) return {};
    return {
        firstName: u.firstName    || '',
        lastName:  u.lastName     || '',
        email:     u.email        || '',
        phone:     u.mobileNumber || '',
        address:   u.address      || '',
        city:      u.city         || '',
    };
});

const instituteFeatures = ref([
    { key: 'maxTutors',        label: 'Maximum tutors' },
    { key: 'maxStudents',      label: 'Maximum students' },
    { key: 'securePayments',   label: 'Secure payments' },
    { key: 'aiFeatures',       label: 'AI features', info: 'AI assistance capabilities' },
    { key: 'pastPapers',       label: 'Past papers' },
    { key: 'inAppChat',        label: 'In app chat' },
]);

function mapLimitValue(value) {
    return value === null || value === undefined ? 'Unlimited' : value.toString();
}

function mapStringFeature(value) {
    if (value === null || value === false || value === 'none') return false;
    // 'all' means the feature is fully included - show a tick rather than the word "All".
    if (value === true || value === 'all') return true;
    if (typeof value === 'string') return value.charAt(0).toUpperCase() + value.slice(1);
    return value;
}

function mapApiPackage(pkg) {
    return {
        id:          pkg._id,
        name:        pkg.name,
        priceLabel:  pkg.pricing.amount.toString(),
        popular:     pkg.isPopular,
        pricingType: pkg.pricing.type,
        features: {
            maxTutors:        mapLimitValue(pkg.limits?.maxTutors),
            maxStudents:      mapLimitValue(pkg.limits?.maxStudents),
            securePayments:   pkg.features?.securePayments  ?? true,
            aiFeatures:       mapStringFeature(pkg.features?.aiFeatures       ?? false),
            pastPapers:       pkg.features?.pastPapers      ?? false,
            inAppChat:        pkg.features?.inAppChat       ?? false,
        },
    };
}

function markActivated(pkg) {
    if (pkg) selectedPackage.value = pkg;
    packageActivated.value = true;
}

function findPackageFromSub(sub) {
    const rawId = sub.packageId?._id ?? sub.packageId;
    if (!rawId) return null;
    return institutePackages.value.find(p => String(p.id) === String(rawId)) ?? null;
}

async function fetchPackages() {
    try {
        const res = await backendApi.get(PACKAGES_ENDPOINTS.GET_ALL, { params: { userType: 'institute' } });
        institutePackages.value = (res.data?.data || []).map(mapApiPackage);
    } catch {
        notifyError('Error', 'Failed to load packages. Please refresh the page.');
    }
}

async function checkHasToken() {
    const res = await payments.getMyToken();
    return res?.data?.data?.hasToken === true;
}

async function waitForSubscriptionActive(maxAttempts = 15, intervalMs = 2000) {
    for (let i = 0; i < maxAttempts; i++) {
        try {
            const res = await subscriptionApi.getMy();
            if (res?.data?.data?.subscription?.status === 'active') return true;
        } catch { /* ignore */ }
        await new Promise(r => setTimeout(r, intervalMs));
    }
    return false;
}

function redirectToPayHere(paymentData, preapprove = false) {
    const isSandbox = paymentData.sandbox === 'true' || paymentData.sandbox === true;
    const url = preapprove
        ? (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_PREAPPROVE_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_PREAPPROVE_URL)
        : (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_URL);
    payments.processPayHereRedirect({ ...paymentData, action: url }, {});
}

async function doSubscribe(packageId, billingData) {
    progressMessage.value    = 'Setting up your subscription...';
    showProgressDialog.value = true;
    try {
        const payload = { packageId, ...(billingData || {}) };
        const res  = await subscriptionApi.subscribe(payload);
        const data = res?.data?.data;

        if (data?.requiresCheckout) {
            showProgressDialog.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, pricingType: 'paid', ts: Date.now() }));
            redirectToPayHere(data.checkoutData, true);
            return;
        }

        if (data?.requiresTokenization) {
            showProgressDialog.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, pricingType: 'free', ts: Date.now() }));
            redirectToPayHere(data.tokenizationData, true);
            return;
        }

        if (data?.status === 'active') {
            showProgressDialog.value = false;
            sessionStorage.removeItem(PENDING_KEY);
            markActivated(selectedPackage.value);
            notifyContrast('Success', 'Your subscription is now active!');
            emit('package-selected', { ...selectedPackage.value });
            return;
        }

        progressMessage.value = 'Waiting for subscription to activate...';
        const activated = await waitForSubscriptionActive();
        showProgressDialog.value = false;
        if (activated) {
            sessionStorage.removeItem(PENDING_KEY);
            markActivated(selectedPackage.value);
            notifyContrast('Success', 'Your subscription is now active!');
            emit('package-selected', { ...selectedPackage.value });
        } else {
            notifyError('Timeout', 'Subscription activation is taking longer than expected. Please refresh and try again.');
        }
    } catch (err) {
        showProgressDialog.value = false;
        const msg = err?.response?.data?.message || '';
        if (msg.toLowerCase().includes('already have an active subscription')) {
            await resolveExistingSubscription();
        } else {
            notifyError('Error', msg || 'Failed to subscribe. Please try again.');
        }
    }
}

async function handlePackageAction(event) {
    selectedPackage.value = event.package;
    actionLoading.value   = true;
    try {
        const hasToken = await checkHasToken();
        if (hasToken) {
            await doSubscribe(selectedPackage.value.id, null);
        } else {
            showBillingDialog.value = true;
        }
    } catch {
        notifyError('Error', 'Something went wrong. Please try again.');
    } finally {
        actionLoading.value = false;
    }
}

async function submitBilling(formData) {
    showBillingDialog.value = false;

    try {
        await updateInstitute({
            firstName:    formData.firstName,
            lastName:     formData.lastName,
            mobileNumber: formData.phone,
            address:      formData.address,
            city:         formData.city,
        });
        if (authStore.user) {
            authStore.user.firstName    = formData.firstName;
            authStore.user.lastName     = formData.lastName;
            authStore.user.mobileNumber = formData.phone;
            authStore.user.address      = formData.address;
            authStore.user.city         = formData.city;
        }
    } catch { /* ignore */ }

    await doSubscribe(selectedPackage.value.id, formData);
}

async function checkPendingSubscription() {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;

    let pending;
    try { pending = JSON.parse(raw); } catch {
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    if (!pending?.packageId) {
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    // If PaymentSuccess set the success flag, poll for webhook activation.
    // Otherwise the payment was cancelled / "Back to site" — clean up, no popup.
    const isSuccess = !!sessionStorage.getItem('instituteOnboardingPaymentSuccess');
    sessionStorage.removeItem('instituteOnboardingPaymentSuccess');

    if (!isSuccess) {
        sessionStorage.removeItem(PENDING_KEY);
        subscriptionApi.cancelPending().catch(() => {});
        return;
    }

    const pkg = institutePackages.value.find(p => p.id === pending.packageId);
    if (pkg) selectedPackage.value = pkg;

    progressMessage.value    = 'Activating your subscription, please wait...';
    showProgressDialog.value = true;

    const activated = await waitForSubscriptionActive();
    showProgressDialog.value = false;
    sessionStorage.removeItem(PENDING_KEY);

    if (activated) {
        markActivated(selectedPackage.value);
        notifyContrast('Success', 'Your subscription is now active!');
        emit('package-selected', { ...selectedPackage.value });
    } else {
        notifyError('Timeout', 'Subscription activation is taking longer than expected. Please refresh and try again.');
    }
}

async function resolveExistingSubscription() {
    try {
        const res = await subscriptionApi.getMy();
        const sub = res?.data?.data?.subscription;
        if (sub?.status === 'active') {
            const pkg = findPackageFromSub(sub);
            markActivated(pkg);
            notifyContrast('Subscription Active', 'You already have an active subscription.');
            emit('package-selected', pkg ? { ...pkg } : {});
        }
    } catch { /* silent */ }
}

const handleContactSales = () => {
    notifyContrast('Coming Soon', 'Please reach out to our sales team for custom plans.');
};

onMounted(async () => {
    packagesLoading.value = true;
    try {
        await fetchPackages();

        const hasPending = !!sessionStorage.getItem(PENDING_KEY);

        try {
            const res = await subscriptionApi.getMy();
            const sub = res?.data?.data?.subscription;
            if (sub?.status === 'active') {
                const pkg = findPackageFromSub(sub);
                markActivated(pkg);
                if (hasPending) {
                    sessionStorage.removeItem(PENDING_KEY);
                    emit('package-selected', pkg ? { ...pkg } : {});
                }
                return;
            }
        } catch { /* no subscription yet */ }

        await checkPendingSubscription();
    } finally {
        packagesLoading.value = false;
    }
});
</script>
